// @flow
import * as React from 'react';
import { Box } from 'grommet';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import MeatballsIcon from 'esm-web-pro/components/layout/sidePanel/icons/MeatballsIcon';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import type { Match, RouterHistory } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import { sortBy } from 'lodash';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { connectAPI } from 'speed-js-react';
import { numberPointsCallbacksPipe } from 'esm-web-pro/containers/pages/network/objects/pipe/Pipe';
import type { Map } from 'esm-web-pro/containers/map/types';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import UseHighlightLine from 'esm-web-pro/hooks/useHighlightLine';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet/lib/types';
import type { VisualisedPipe } from 'esm-web-pro/containers/pages/network/objects/gisline/GisLineContent';
import ContentLabel from 'esm-web-pro/components/layout/sidePanel/ContentLabel';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';

type Props = {
    continuousPipe: Object,
    intl: IntlShape,
    history: RouterHistory,
    match: Match,
    clickedRow: string,
    setClickedRow: Function,
    editor: BaseEditor,
    getPipePath: (id: string, map: Map) => Promise<VisualisedPipe>,
    map: Object,
    leaflet: LeafletContext,
}

const ContinuousPipeContent = (props: Props) => {
    const {
        continuousPipe, intl, match, history, clickedRow, setClickedRow, editor, getPipePath, map, leaflet,
    } = props;

    const highlightPipe = async (pipe) => {
        setClickedRow(`Pipe ${pipe.id}`);

        editor.unregisterAllLoadingCallback();
        const vPipe = await getPipePath(pipe.identity, map);
        const pointCallbacks = numberPointsCallbacksPipe(vPipe);

        const pipeCoords = vPipe.path.map((c) => [c[1], c[0]]);

        const highlightPipeCallback = UseHighlightLine(pipeCoords,
            [{
                color: vPipe.color, dashArray: '20', className: 'route-line',
            },
            {
                color: vPipe.color, dashArray: null, className: '',
            }]);
        if (leaflet.map) {
            editor.registerLoadingCallback(highlightPipeCallback);
            editor.registerMultipleLoadingCallbacks(pointCallbacks);

            leaflet.map.highlightedGeometry = {
                bounds: pipeCoords, center: null,
            };
            leaflet.map.panInsideBounds(pipeCoords);
        }
        editor.reloadFeatures();
    };

    return (
        <Box pad={{ vertical: '20px' }} height={{ min: '100%' }}>
            <ContentLabel>
                <FormattedMessage id="continuouspipe-content-pipes" defaultMessage="Pipes" />
            </ContentLabel>
            <Box>
                {sortBy(continuousPipe.pipes, 'path_sequence').map((pipe) => (
                    <React.Fragment key={pipe.uuid}>
                        <ContentRow
                            padLevel={1}
                            onClick={() => highlightPipe(pipe)}
                            clickedRowIdentity={clickedRow}
                            rowId={`Pipe ${pipe.id}`}
                            actions={(
                                <RowActions
                                    iconPadding="3px"
                                    gapBetweenActions="medium"
                                    actions={[{
                                        key: `${continuousPipe.id}-${pipe.id}-more`,
                                        icon: <MeatballsIcon />,
                                        size: '20px',
                                        width: '3px',
                                        callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                                            e.stopPropagation();
                                            const path = match.url.split('/layer');
                                            history.push(`${path[0]}/layer/pipe/${pipe.identity}`);
                                        },
                                        tooltip: intl.formatMessage(actionMessages.more),
                                    }]}
                                />
                            )}
                        >
                            <ValueText>
                                <FormattedMessage
                                    id="route-pipe-title"
                                    defaultMessage="Pipe {pipeid}"
                                    values={{
                                        pipeid: pipe.ref,
                                    }}
                                />
                            </ValueText>
                        </ContentRow>
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    getPipePath: {
        send: (id: string, map: Object) => {
            if (map.is_draft) {
                return apiClient.resources['pipe.geo']
                    .path(map.uuid, id, { is_draft: map.uuid });
            }
            return apiClient.resources['pipe.geo']
                .path(map.uuid, id);
        },
    },
});

export default withLeaflet(connectAPI(apiCallback)(withRouter(injectIntl(ContinuousPipeContent))));
